<template>
  <b-form-group :label="label" :label-for="id">
    <v-select
      :id="id"
      v-model="selectedValue"
      :options="items"
      :reduce="(item) => item.id"
      :label="labelKey"
      :placeholder="placeholder"
      :disabled="disabled"
      :clearable="clearable"
      @search="onSearch"
      @input="handleInput"
      :multiple="multiple"
      :searchable="true"
      :filterable="false"
      :class="`resource-dropdown ${error ? 'is-invalid' : ''}`"
    >
      <template #spinner>
        <div v-if="isLoading" class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </template>

      <template #no-options>
        {{ isLoading ? 'Loading...' : 'No options available' }}
      </template>

      <template #option="option">
        {{ option[labelKey] }}
      </template>

      <template #selected-option="option">
        {{ displayValue ? displayValue[labelKey] : option[labelKey] }}
      </template>
    </v-select>

    <small v-if="error" class="text-danger">{{ error }}</small>
  </b-form-group>
</template>

<script>
import store from '@/store';
import { APIService } from '@/utils/api';
import { RESOURCES } from '@/utils/constants';
import { computed, onMounted, ref, watch } from '@vue/composition-api';
import { BFormGroup, BFormInvalidFeedback } from 'bootstrap-vue';
import vSelect from 'vue-select';

export default {
  name: 'ResourceDropdown',

  components: {
    BFormGroup,
    BFormInvalidFeedback,
    'v-select': vSelect,
  },

  props: {
    // The API resource name (e.g., 'Warehouses', 'Venues')
    resource: {
      type: String | Object,
      required: true,
    },
    itemsKey: {
      type: String,
      default: 'items',
    },
    labelKey: {
      type: String,
      default: 'name',
    },
    // The selected value (for v-model)
    modelValue: {
      type: [Number, String, null],
      default: null,
    },
    // The selected value (for v-model)
    value: {
      type: [Number, String, Array, null],
      default: null,
    },
    // Input label
    label: {
      type: String,
      default: '',
    },
    // Unique identifier for the input
    id: {
      type: String,
      default: () => `dropdown-${Math.random().toString(36).substr(2, 9)}`,
    },
    // Placeholder text
    placeholder: {
      type: String,
      default: 'Select an option',
    },
    // Validation state
    state: {
      type: Boolean,
      default: null,
    },
    // Error message
    error: {
      type: String,
      default: null,
    },
    // Disabled state
    disabled: {
      type: Boolean,
      default: false,
    },
    // Additional params for the API call
    params: {
      type: Object,
      default: () => ({}),
    },
    // Custom text field for the options (default is 'name')
    textField: {
      type: String,
      default: 'name',
    },
    // Allow clearing the selection
    clearable: {
      type: Boolean,
      default: true,
    },
    // Minimum characters for search
    minSearch: {
      type: Number,
      default: 1,
    },
    // Multiple selection
    multiple: {
      type: Boolean,
      default: false,
    },
    businessRequired: {
      type: Boolean,
      default: true,
    },
    excludeFilter: {
      type: [Number, Array],
      default: null,
    },
    searchKey: {
      type: String,
      default: 'searchTerm',
      required: false,
    },
  },

  setup(props, { emit }) {
    const items = ref([]);
    const isLoading = ref(false);
    const selectedValue = ref(Array.isArray(props.value) ? [...props.value] : props.value);
    const showError = ref(!!props.error);
    const selectedItemCache = ref(null); // Cache for single selected item
    const multipleItemsCache = ref([]); // Cache for multiple selected items
    let searchTimeout = null;

    const { itemsKey, labelKey } = props;

    // Get businessID from store
    const businessID =
      store.state.user?.businessID ||
      store.state.businessID ||
      store.state.userData?.businessID ||
      1;

    // Get userID from store
    const userID = store.state.user?.id || store.state.userData?.id || 1;

    // Computed property to get display value for selected item
    const displayValue = computed(() => {
      if (!selectedValue.value) return null;

      // Handle multiple selection
      if (props.multiple && Array.isArray(selectedValue.value)) {
        return multipleItemsCache.value.length > 0 ? multipleItemsCache.value : null;
      }

      // Handle single selection
      // First check if we have the selected item cached
      if (
        selectedItemCache.value &&
        selectedItemCache.value.id &&
        selectedItemCache.value.id.toString() === selectedValue.value.toString()
      ) {
        return selectedItemCache.value;
      }

      // Find the selected item in the loaded items
      const selectedItem = items.value.find(
        (item) => item && item.id && item.id.toString() === selectedValue.value.toString()
      );

      if (selectedItem) {
        // Cache the selected item for future use
        selectedItemCache.value = selectedItem;
        return selectedItem;
      }

      // Handle special cases for known resources
      if (
        props.resource &&
        typeof props.resource === 'object' &&
        props.resource.name === 'Currencies'
      ) {
        if (selectedValue.value === 2) {
          const currencyItem = { id: 2, [labelKey]: 'PKR', name: 'PKR' };
          selectedItemCache.value = currencyItem;
          return currencyItem;
        }
      }

      // If we have a cached selected item but it doesn't match current value,
      // it means the value has changed - clear the cache
      if (selectedItemCache.value) {
        selectedItemCache.value = null;
      }

      // Return null instead of "Loading..." to prevent showing loading text
      // The v-select component will handle the display appropriately
      return null;
    });

    const fetchItems = async (search = '') => {
      isLoading.value = true;
      try {
        const defaultParams = {
          pageNumber: 1,
          pageSize: 10, // Limit to 10 items for better performance
          [props.searchKey]: search,
          ...props.params,
        };

        let resourceObj = props.resource;

        if (typeof resourceObj === 'string') {
          resourceObj = Object.values(RESOURCES).find((resource) => resource.name === resourceObj);
        }

        const response = await APIService.getByPagination(resourceObj, businessID, defaultParams);

        if (response?.data?.status === 'success') {
          const responseData = response.data.data;
          let fetchedItems = [];

          // Check if the itemsKey exists directly in responseData
          if (responseData && responseData[itemsKey]) {
            fetchedItems = responseData[itemsKey];
          }
          // Check if responseData itself is the items array (for backward compatibility)
          else if (Array.isArray(responseData)) {
            fetchedItems = responseData;
          }
          // Check if we need to use 'results' key as fallback
          else if (responseData && responseData.results) {
            fetchedItems = responseData.results;
          } else {
            console.warn(
              `Could not find data with key "${itemsKey}" in the response:`,
              responseData
            );
            fetchedItems = [];
          }

          if (props.excludeFilter) {
            fetchedItems = fetchedItems.filter((item) => !props.excludeFilter.includes(item.id));
          }

          // Limit to 10 items maximum for performance
          fetchedItems = fetchedItems.slice(0, 10);

          // Add "All" option if requested in params
          if (props.params?.includeAllOption) {
            const allOption = {
              id: props.params.allOptionId || '0',
              [props.labelKey]: props.params.allOptionLabel || `All ${props.resource}`,
            };
            // For name field compatibility
            if (props.labelKey !== 'name') {
              allOption.name = props.params.allOptionLabel || `All ${props.resource}`;
            }

            items.value = [allOption, ...fetchedItems];
          } else {
            items.value = fetchedItems;
          }
        } else if (response.data && itemsKey in response.data) {
          items.value = response.data[itemsKey].slice(0, 10); // Limit to 10 items
        }
      } catch (error) {
        console.error(`Error fetching ${props.resource}:`, error);
        items.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    // Function to fetch a specific item by ID
    const fetchSelectedItem = async (id) => {
      if (!id) return null;

      // Check if we already have this item cached
      if (props.multiple) {
        const cachedItem = multipleItemsCache.value.find(
          (item) => item && item.id && item.id.toString() === id.toString()
        );
        if (cachedItem) return cachedItem;
      } else {
        if (
          selectedItemCache.value &&
          selectedItemCache.value.id &&
          selectedItemCache.value.id.toString() === id.toString()
        ) {
          return selectedItemCache.value;
        }
      }

      try {
        let resourceObj = props.resource;

        if (typeof resourceObj === 'string') {
          resourceObj = Object.values(RESOURCES).find((resource) => resource.name === resourceObj);
        }

        const response = await APIService.getById(resourceObj, id, businessID, userID);

        if (response?.data?.status === 'success') {
          const item = response.data.data;

          // Cache the selected item
          if (props.multiple) {
            // For multiple selection, add to cache if not already present
            const existingIndex = multipleItemsCache.value.findIndex(
              (cachedItem) =>
                cachedItem && cachedItem.id && cachedItem.id.toString() === id.toString()
            );
            if (existingIndex === -1) {
              multipleItemsCache.value.push(item);
            }
          } else {
            selectedItemCache.value = item;
          }

          return item;
        }
      } catch (error) {
        console.error(`Error fetching selected item by ID ${id}:`, error);
      }

      return null;
    };

    const debounce = (fn, delay) => {
      return (...args) => {
        if (searchTimeout) {
          clearTimeout(searchTimeout);
        }
        searchTimeout = setTimeout(() => {
          fn.apply(null, args);
        }, delay);
      };
    };

    const performSearch = async (search) => {
      // Always fetch items, even with empty search to show initial 10 items
      await fetchItems(search);
    };

    // Create debounced search function with shorter delay for better UX
    const debouncedSearch = debounce(performSearch, 200);

    const onSearch = (search) => {
      isLoading.value = true;
      debouncedSearch(search);
    };

    const handleInput = (value) => {
      selectedValue.value = value;

      // Handle caching for both single and multiple selection
      if (props.multiple && Array.isArray(value)) {
        // For multiple selection, update the cache
        multipleItemsCache.value = value
          .map((id) => {
            // First check in multiple cache
            let item = multipleItemsCache.value.find(
              (cachedItem) =>
                cachedItem && cachedItem.id && cachedItem.id.toString() === id.toString()
            );

            // If not in cache, try to find in items list
            if (!item) {
              item = items.value.find(
                (listItem) => listItem && listItem.id && listItem.id.toString() === id.toString()
              );
            }

            return item;
          })
          .filter((item) => item !== undefined);
      } else if (value !== null && value !== undefined) {
        // For single selection
        const selectedItem = items.value.find(
          (item) => item && item.id && item.id.toString() === value.toString()
        );
        if (selectedItem) {
          selectedItemCache.value = selectedItem;
        }
      } else {
        // Clear caches when no value is selected
        selectedItemCache.value = null;
        multipleItemsCache.value = [];
      }

      emit('input', value);

      // Emit the selected item(s) as well
      if (props.multiple && Array.isArray(value)) {
        emit('item-selected', multipleItemsCache.value);
      } else if (value !== null && value !== undefined) {
        const selectedItem = items.value.find(
          (item) => item && item.id && item.id.toString() === value.toString()
        );
        emit('item-selected', selectedItem || selectedItemCache.value);
      } else {
        emit('item-selected', null);
      }
    };

    // Watch for resource or params changes to refetch items
    watch([() => props.resource, () => props.params], () => {
      fetchItems();
    });

    // Watch for external value changes
    watch(
      () => props.value,
      async (newValue, oldValue) => {
        selectedValue.value = newValue;

        // Handle multiple selection
        if (props.multiple && Array.isArray(newValue)) {
          // Clear and rebuild cache for multiple selection
          multipleItemsCache.value = [];

          // For multiple selection, check if we have all items cached
          for (const id of newValue) {
            if (id !== null && id !== undefined) {
              const foundItem = items.value.find(
                (item) => item && item.id && item.id.toString() === id.toString()
              );
              if (!foundItem) {
                // If item not found in current items, fetch it by ID
                await fetchSelectedItem(id);
              } else {
                // Add found item to cache
                multipleItemsCache.value.push(foundItem);
              }
            }
          }
        } else if (newValue !== null && newValue !== undefined) {
          // Handle single selection
          const foundItem = items.value.find(
            (item) => item && item.id && item.id.toString() === newValue.toString()
          );
          if (foundItem) {
            selectedItemCache.value = foundItem;
          } else {
            // If item not found in current items and we don't have it cached, fetch it by ID
            if (
              !selectedItemCache.value ||
              selectedItemCache.value.id.toString() !== newValue.toString()
            ) {
              await fetchSelectedItem(newValue);
            }
          }
        } else {
          // Clear all caches when no value is selected
          selectedItemCache.value = null;
          multipleItemsCache.value = [];
        }
      }
    );

    onMounted(async () => {
      // If there's an initial value, fetch the selected item first
      if (props.value !== null && props.value !== undefined) {
        if (props.multiple && Array.isArray(props.value)) {
          // For multiple selection, fetch each selected item
          for (const id of props.value) {
            await fetchSelectedItem(id);
          }
        } else {
          // For single selection
          await fetchSelectedItem(props.value);
        }
      }

      // Then fetch the dropdown items
      fetchItems();
    });

    return {
      items,
      isLoading,
      selectedValue,
      showError,
      handleInput,
      onSearch,
      displayValue,
      fetchSelectedItem,
      selectedItemCache,
      multipleItemsCache,
    };
  },
};
</script>

<style>
.is-invalid .vs__dropdown-toggle {
  border: 1px solid #ea5455 !important;
}
</style>
